import { GameConfig, Section, TransitionContent, QuizContent, MemoryContent } from '../domain/entities/GameConfig';
import { MediaContentImpl } from '../domain/entities/MediaContent';
import { loadMemories } from '../utils/memoryLoader';


// Azure Storage configuration
const STORAGE_BASE_URL = "https://ukskristinasa.blob.core.windows.net/valentines2025";
const STORAGE_SAS_TOKEN = process.env.REACT_APP_AZURE_STORAGE_SAS_TOKEN || "sv=2024-11-04&ss=b&srt=sco&sp=rlaf&se=2026-09-01T00:45:42Z&st=2025-06-04T16:45:42Z&spr=https&sig=6%2BEVE%2BbmtrgrRKNdZZwNo34ZGkc95udVqzst6OXuFM0%3D";


// Helper function to convert local paths to blob storage URLs
function toBlobUrl(localPath: string): string {
    // Remove leading slash if present
    const path = localPath.startsWith('/') ? localPath.substring(1) : localPath;
    // For assets/memories paths, extract just the relevant part

    const relativePath = path.includes('assets/memories/images/') 
        ? path.split('assets/memories/images/')[1] 
        : path.includes('assets/memories/videos/')
            ? path.split('assets/memories/videos/')[1]
            : path;

    // Split the path to encode each segment properly (preserves directory structure)
    const segments = relativePath.split('/');
    const encodedSegments = segments.map(segment => encodeURIComponent(segment));
    const encodedPath = encodedSegments.join('/');
    
    return `${STORAGE_BASE_URL}/${encodedPath}?${STORAGE_SAS_TOKEN}`;}

// Create a function to initialize the game content
export async function initializeGameContent(): Promise<GameConfig> {
    // Load memories
    const memories = await loadMemories();
    
    // Convert memories to media content for display
    const mediaContent = memories.map(memory => new MediaContentImpl(
        memory.id,
        memory.mediaType,  // Use the mediaType property from Memory objects
        memory.mediaUrl || '',
        memory.title,
        memory.description
    ));

    // Define content types
    const introContent: TransitionContent = {
        id: "intro-content",
        type: "TRANSITION",
        title: "The Beginning",
        description: "Where it all started...",
        text: "Let's start from the beninging...",
        media: MediaContentImpl.createYouTubeClip(
            "beninging-clip",
            "https://youtu.be/fWKB8zdVM-U",
            "In the beninging",
            0,
            20,
            "Starting from the beninging...",
            undefined,
            true,
            true,
            false
        ),
        continueText: "Skip Intro",
        replayText: "Watch Again",
        continuePrompt: "Okay lets go.",
        replayPrompt: "Want to watch it again from the beninging?"
    };

    const firstMessageContent: QuizContent = {
        id: "first-message-content",
        type: "QUIZ",
        title: "The First Message",
        description: "Remember how it all started?",
        questions: [
            {
                id: "first-message-themes",
                text: "What are the 2 first things that stood out to me about you?",
                type: "MULTIPLE_CHOICE",
                options: [
                    "Your grace and elegance",
                    "Your love for Mrs Trump",
                    "Your style an fashun",
                    "Your love for memes"
                ],
                correctAnswer: "Your grace and elegance",
                hint: "It involves my first message to you.",
                feedback: {
                    correct: "I still remember rewriting that message like 5 times.",
                    incorrect: "Nope. But I started with a compliment."
                }
            }
        ]
    };

    const firstDateContent: QuizContent = {
        id: "first-date-content",
        type: "QUIZ",
        title: "First Date Memories",
        description: "What did you say just before?",
        questions: [
            {
                id: "after-date-message",
                text: "What did you say just before this moment?",
                type: "MULTIPLE_CHOICE",
                options: [
                    "The way I would fuck him",
                    "He's so cute",
                    "I really like him",
                    "I want to see him again"
                ],
                correctAnswer: "The way I would fuck him",
                media: new MediaContentImpl(
                    "voice-note-image",
                    "IMAGE",
                    toBlobUrl("/assets/memories/images/The Way I Would Fuck Him.png"),
                    "Voice note screenshot",
                    "Screenshot of the voice note sent after our first date"
                ),
                feedback: {
                    correct: "Oh, how I wanted to bend you over and fuck you tirelessly that night...",
                    incorrect: "It was a bit more spicy than that, darling.."
                }
            },
            {
                id: "best-behaved",
                text: "Who is really the best behaved girl?",
                type: "MULTIPLE_CHOICE",
                options: [
                    "Mika",
                    "Kristina",
                    "Both equally",
                    "Donald Trump"
                ],
                correctAnswer: "Mika",
                hint: "Be honest",
                feedback: {
                    correct: "Mika definitely gives me less cheek ;)",
                    incorrect: "Honesty is the best policy, Kristina"
                }
            }
        ]
    };

    const memeContent: QuizContent = {
        id: "meme-content",
        type: "QUIZ",
        title: "Our Favorite Memes",
        description: "Only quality content with us darling.",
        questions: [
            {
                id: "why-are-you-gay",
                text: "What is the correct answer?",
                type: "MULTIPLE_CHOICE",
                options: [
                    "Why are you gay?",
                    "Who says I'm gay?",
                    "You are gay.",
                    "All of the above"
                ],
                correctAnswer: "Why are you gay?",
                media: MediaContentImpl.createYouTubeClip(
                    "why-gay-clip",
                    "https://youtu.be/4n4rBrs5-LY",
                    "Why are you gay?",
                    0,
                    10,
                    "Famous Uganda interview meme",
                    undefined,
                    true,
                    true,
                    false
                ),
                feedback: {
                    correct: "Why are you gay, Kristina?",
                    incorrect: "Wh..."
                }
            },
            {
                id: "lord-of-rings",
                text: "What's the only correct way to say Lord of the Rings?",
                type: "FILL_BLANK",
                correctAnswer: "Lord of the Big Dick",
                hint: "Think about what the ring does... ;)",
                textInputConfig: {
                    caseSensitive: false,
                    allowedVariations: ["lord of the big dick", "lord of big dick"],
                    fuzzyMatch: true
                },
                feedback: {
                    correct: "Correct! Though I love it when you tell me who the Real Lord is.",
                    incorrect: "Think back to when we were in bed at mine...",
                    almost: "Close."
                }
            }
        ]
    };

    const moreMemeContent: QuizContent = {
        id: "more-meme-content",
        type: "QUIZ",
        title: "Lets keep it rolling.",
        description: "Lets keep it going.",
        questions: [
            {
                id: "suitcase-love",
                text: "And the other?",
                type: "TEXT",
                correctAnswer: "I like my suitcase",
                media: MediaContentImpl.createYouTubeClip(
                    "suitcase-clip",
                    "https://youtu.be/cBrfEy9pXKw",
                    "I like my suitcase",
                    0,
                    20,
                    "The iconic suitcase moment",
                    undefined,
                    true,
                    true,
                    false
                ),
                textInputConfig: {
                    caseSensitive: false,
                    allowedVariations: ["i love my suitcase", "I like my suitcase", "i really like my suitcase"]
                },
                feedback: {
                    correct: "You know, I might get a suitcase again.",
                    incorrect: "What does he love, so much?",
                    almost: "Close."
                }
            }
        ]
    };

    const spicyContent: QuizContent = {
        id: "spicy-content",
        type: "QUIZ",
        title: "Spicy Memories",
        description: "Our intimate moments...",
        questions: [
            {
                id: "bar-video-caption",
                text: "What was the caption to this video, you pervert? ;)",
                type: "MULTIPLE_CHOICE",
                options: [
                    "Your view is breathtaking",
                    "I have a really nice view",
                    "That's one view I can't get enough of",
                    "Boom shackalacka"
                ],
                correctAnswer: "I have a really nice view",
                media: new MediaContentImpl(
                    "bar-video",
                    "VIDEO",
                    toBlobUrl("/assets/memories/videos/WhatsApp Video 2024-12-17 at 21.18.36.mp4"),
                    "Bar Video",
                    "That view though..."
                ),
                feedback: {
                    correct: "I think society accepts perversion if its mutually reciprocated, so I think we won't be hung out on a string anytime soon.",
                    incorrect: "Not quite the level of perversion I showed 😏"
                }
            },
            {
                id: "never-bite",
                text: "Pick the correct answer",
                type: "MULTIPLE_CHOICE",
                options: [
                    "Never bite the hand that feeds you",
                    "Never bite the hand of the one that fingers you",
                    "Always cherish the hand that caresses you",
                    "Don't cut off the hand that holds you"
                ],
                correctAnswer: "Never bite the hand of the one that fingers you",
                media: MediaContentImpl.createYouTubeClip(
                    "never-bite-video",
                    "https://youtu.be/LJ-vrMOU8xU",
                    "Never..",
                    0,
                    5,
                    "Meme culture just always gets it right.",
                    undefined,
                    true,
                    true,
                    false
                ),
                feedback: {
                    correct: "If you follow that advice, we'll be good as dandy, babe. ;)",
                    incorrect: "Kristina's spicier version."
                }
            },
            {
                id: "underwear-response",
                text: "What was my first response to: 'It's a pity you can't see what underwear I'm wearing tonight'?",
                type: "MULTIPLE_CHOICE",
                options: [
                    "🙏🏽 Lord give me strength in this moment",
                    "All i'm thinking about right now is unbuttoning you…\nThe feeling of your lips against mine…\nThe feeling of your breath on my shoulders when you kiss my neck…",
                    "I want to be with you right now",
                    "I want your hair in my face"
                ],
                correctAnswer: "🙏🏽 Lord give me strength in this moment",
                media: new MediaContentImpl(
                    "underwear-message",
                    "IMAGE",
                    toBlobUrl("/assets/memories/images/8656B9FC-F7F8-4546-BC13-9CCAE7DD7349_4_5005_c.jpeg"),
                    "Testing my strength",
                    "A moment of prayer was needed 🙏"
                ),
                feedback: {
                    correct: "Keep them coming. I love the tease.",
                    incorrect: "That message definitely had me praying 🙏"
                }
            }
        ]
    };

    const memoryContent: MemoryContent = {
        id: "memory-content",
        type: "MEMORY",
        title: "Our Memories",
        description: "A collection of our special moments",
        media: mediaContent,
        continueText: "Continue",
        replayText: "Watch Again",
        continuePrompt: "Ready to move forward?",
        replayPrompt: "Want to see our memories again?"
    };

    const reflectionContent: TransitionContent = {
        id: "reflection-content",
        type: "TRANSITION",
        title: "Looking Back, Moving Forward",
        description: "A few words before the final question...",
        text:  `Kristina,

I deeply appreciate the way you allow yourself to be vulnerable with me. I know this hasn’t been easy—there’s been anxiety, uncertainty, and intensity woven into our story. But through it all, I’ve watched you grow, step by step, into your strength. I remember, in the beginning, when you told me you didn’t want me to see you as ill, or to hold that image of you at all. The truth is, even if that were the case (which it isn’t), it wouldn’t change how I feel. It wouldn’t change the love I have for you—the way I do.

When you speak about Ukraine, about your home, your family, your fears—I wish I had the power to shift the world for you. Because for you, I would. You opened my eyes to the double standards, the narratives that shape how we perceive suffering, and before I met you, I didn’t feel as deeply about it as I do now.

You taught me about Stalin without ever meaning to—just by sharing your family’s history. You’ve shown me how much growth is possible in such a short time. And it’s terrifying how much I see myself in you. But as I’ve said before, you are not just me in another body. You are your own force—your own passions, your own beliefs, your own joys, memories, and heartbreaks. And you are breathtakingly, achingly gorgeous.

I think back to that moment—our second date, at mine—when I half-joked, half-confessed, “I’d put a baby in her.” Even then, I caught myself, wondering if it was too much to say. Because I have never felt that before. And it isn’t just the affection, or the way you ride me like a horse, or the way you cook for me—it’s the way you challenge me, the way you make me grow.

And maybe that’s what defines us: growth, and mutual love.

We have our differences, yet you help me find common ground. And I know—no man walks the path of life alone. I have built, and will continue to build, the life I want, the future I envision, the legacy I am creating. But behind every great man is a great woman.

So, I think it’s time we make this official.`,
        continueText: "Okay lets go",
        replayText: "Repeat memories",
        continuePrompt: "Continue?",
        replayPrompt: "Want to take another look at our pics?"
    };

    const finalContent: QuizContent = {
        id: "final-content",
        type: "QUIZ",
        title: "The Final Question",
        description: "Time for something important...",
        questions: [
            {
                id: "final-question",
                text: "Will you be my girlfriend? <type>",
                type: "MULTIPLE_CHOICE",
                options: [
                    "Yes",
                    "ⁿᵒ"
                ],
                correctAnswer: "Yes",
                feedback: {
                    correct: "I'm not even sure what a girlfriend is.",
                    incorrect: "Are you sure? That button was pretty small... 😏"
                }
            }
        ]
    };

    // Define sections
    const sections: Section[] = [
        {
            id: "intro-section",
            title: "In the beninging...",
            description: "How it all started",
            levels: [
                {
                    id: "intro-video",
                    title: "In the beninging",
                    description: "Let's start from the beninging...",
                    content: introContent
                }
            ]
        },
        {
            id: "first-connection",
            title: "In the beninging",
            description: "How we first connected.",
            levels: [
                {
                    id: "first-message",
                    title: "The First Message",
                    description: "Remember how it all started?",
                    content: firstMessageContent
                },
                {
                    id: "first-date",
                    title: "First Date Memories",
                    description: "What did you say just before?",
                    content: firstDateContent
                }
            ]
        },
        {
            id: "inside-jokes",
            title: "High Quality Content",
            description: "The memes and moments that give us great joy.",
            levels: [
                {
                    id: "meme-quotes",
                    title: "Our Favorite Memes",
                    description: "Only quality content with us darling.",
                    content: memeContent
                },
                {
                    id: "more-memes",
                    title: "Lets keep it rolling.",
                    description: "Lets keep it going.",
                    content: moreMemeContent
                }
            ]
        },
        {
            id: "spicy-memories",
            title: "Our Spicy Moments",
            description: "Some of our more... intimate exchanges 😏",
            levels: [
                {
                    id: "spicy-level",
                    title: "Spicy Memories",
                    description: "Our intimate moments...",
                    content: spicyContent
                }
            ]
        },
        {
            id: "memory-montage",
            title: "Our Journey Together",
            description: "Let's have a look at our journey together.",
            levels: [
                {
                    id: "memories-slideshow",
                    title: "The Last 2 Months",
                    description: "It just doesn't feel like its been only 2 months.",
                    content: memoryContent
                }
            ]
        },
        {
            id: "pre-final",
            title: "A Moment of Reflection",
            description: "Before we move forward...",
            levels: [
                {
                    id: "reflection",
                    title: "A Moment of Reflection",
                    description: "A few words before the end-game...",
                    content: reflectionContent
                }
            ]
        },
        {
            id: "final-section",
            title: "The Big Question",
            description: "The Endgame",
            levels: [
                {
                    id: "proposal",
                    title: "A Question",
                    description: "",
                    content: finalContent
                }
            ]
        }
    ];
    
    return {
        title: "Our Journey Together",
        description: "A journey through our memories and moments",
        sections,
        totalLevels: sections.reduce((total, section) => total + section.levels.length, 0),
        mediaContent,
        memories
    };
}

// Export a default empty config that will be replaced with the initialized one
export const gameContent: GameConfig = {
    title: "Loading...",
    description: "Loading your special moments...",
    sections: [],
    totalLevels: 0,
    mediaContent: [],
    memories: []
};

// You can easily add more sections, levels, and questions by adding them to this configuration
// Each section can have multiple levels, and each level can have multiple questions
// The structure is flexible and can be extended with more content